import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Container, Button, Spinner, Row, Col } from "reactstrap";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { useIpInventoryService } from "modules/Pharmacy/IPinventory/api/useIpInventoryService";
import { formatDateSplit, showToastMessage, parseLocalDate } from "../../../../common/service/CommonService";
import CommonForm from "Components/Common/CommonForm";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { IP_PATHS } from "Routes/paths";
import {
  IP_UNIT_OPTIONS,
  Light_Condition_OPTIONS,
  MANUFACTURER_LABEL_OPTIONS,
  MODE_OF_RECEIPT_OPTIONS,
  PACKING_CONDITION_OPTIONS,
  DOSING_FORM_OPTIONS,
  loadCodelistsDetailId,
} from "api/modules/common.option";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import { CheckListQuestion } from "modules/Library/Checklist/helpers/checklist.types";
import { IpInventoryFormValues, ContainerItem, CoaDetailRow } from "modules/Pharmacy/IPinventory/helpers/ipInventory.types";
import {
  getStudyProductTopFields,
  getStudyProductCategoryFields,
  getProductDetailsFields,
  getBatchDeliveryFields,
  getStorageConditionFields,
  getDataLoggerFields,
  getPharmacyQuarantineFields,
  getCoaTopFields,
  getIPCategoryLabel,
  SELECT_QUERY_FIELDS,
} from "../helpers/ipInventory.constant";
import {
  calculateDateRestrictions,
  checkIsFieldDisabled,
  buildUpdatePayload,
  useIpStepPersistence,
} from "../helpers/ipInventory.helper";
import CoaDetailsTable from "../components/CoaDetailsTable";
import ContainerManagementSection from "../components/ContainerManagementSection";
import AttachmentManagementSection from "../components/AttachmentManagementSection";
import IpInventoryStepper from "../components/IpInventoryStepper";
import { queryClient } from "api/queryClient";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import IpInventoryAcceptance from "./IpInventoryAcceptance";
import FullPageLoader from "Components/Common/FullPageLoader";
import { validations } from "validations/FormValidations";
import SectionCard from "Components/Common/SectionCard";
import { BreadcrumbContext } from "Layouts/Header";
import { useGlobalStudy } from "context/StudyContext";

const EditIpInventory: React.FC = () => {
  /* ------------------------------------------------------------------------ */
  /* 1. ROUTING, CONTEXT & STEPPERS                                           */
  /* ------------------------------------------------------------------------ */
  const navigate = useNavigate();
  const { currentStep, handleStepChange, searchParams } = useIpStepPersistence();
  const { id: paramId } = useParams();
  const editId = paramId || searchParams.get("id");
  const selectedreceiptnumber = sessionStorage.getItem("receiptnumber");
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { selectedStudy: globalStudy } = useGlobalStudy();

  /* ------------------------------------------------------------------------ */
  /* 2. LOCAL STATE & REFS                                                    */
  /* ------------------------------------------------------------------------ */
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<number[]>([]);
  const [containersList, setContainersList] = useState<ContainerItem[]>([]);
  const [containerError, setContainerError] = useState<string | null>(null);
  const [formData, setFormData] = useState<IpInventoryFormValues | null>(null);
  const [coaList, setCoaList] = useState<CoaDetailRow[]>([]);
  const [codelistsLoaded, setCodelistsLoaded] = useState(false);
  const [coaError, setCoaError] = useState<string | null>(null);

  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const originalContainersRef = useRef<any[]>([]);
  const originalCoaRef = useRef<CoaDetailRow[]>([]);
  const validateContainersRef = useRef<(() => boolean) | null>(null);
  const isPopulatedRef = useRef(false);
  const isCoaPopulatedRef = useRef(false);

  /* ------------------------------------------------------------------------ */
  /* 3. REACT HOOK FORM SETUP                                                 */
  /* ------------------------------------------------------------------------ */
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    register,
    formState: { errors },
  } = useForm<IpInventoryFormValues>({
    mode: "onChange",
  });

  /* ------------------------------------------------------------------------ */
  /* 4. FORM WATCHERS                                                         */
  /* ------------------------------------------------------------------------ */
  const selectedStudyCode = watch("studycode");
  const selectedmanufacturingdate = watch("manufacturingdate");
  const selectedexpirydate = watch("expirydate");
  const selecteddateofreceipt = watch("dateofreceipt");
  const selectedquarantinestartdate = watch("quarantinestartdate");
  const selectedquarantineenddate = watch("quarantineenddate");
  const selectedcoareceiptdate = watch("coareceiptdate");
  const isDataLogger = watch("isdatalogger");
  const selectedLoggerPrint = watch("loggerprintobtained");
  const selectedTempExcursion = watch("tempexcursionacceptable");
  const selectedTypeOfDate = watch("typeofdate");
  const batchorlot = watch("batchorlot");
  const qtyForStudy = watch("qtyforstudy");
  const qtyForRetention = watch("qtyforretention");
  const selectedPackingCondition = watch("packingcondition");

  /* ------------------------------------------------------------------------ */
  /* 5. API & SERVICES QUERIES                                                */
  /* ------------------------------------------------------------------------ */
  const { UpdateMutation, SelectIdQuery } = useIpInventoryService({
    ipid: currentStep === 1 ? editId : null,
    studyid: globalStudy?.studyid,
    receiptnumber: selectedreceiptnumber,
  });
  const selectQuery = SelectIdQuery;

  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });

  /* ------------------------------------------------------------------------ */
  /* 6. (useMemo)                                   */
  /* ------------------------------------------------------------------------ */
  const studyOptions = useMemo(
    () =>
      (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studycode,
        studyid: study.studyid,
        sponsorname: study.sponsorname,
        sponsorid: study.sponsorid,
        ipcategory: study.ipcategory,
      })) || [],
    [studySelectAllQuery.data]
  );

  const selectedStudy = useMemo(
    () => studyOptions.find((s: any) => s.value === selectedStudyCode),
    [selectedStudyCode, studyOptions]
  );

  const ipCategoryOptions = useMemo(() => {
    const codes = selectedStudy?.ipcategory || [];
    return codes.map((code: string) => ({
      label: getIPCategoryLabel(code),
      value: code,
    }));
  }, [selectedStudy]);

  const totalContainerQty = useMemo(() => {
    return containersList.reduce((acc, curr) => acc + (Number(curr.initialquantity) || 0), 0);
  }, [containersList]);

  const dateRestrictions = useMemo(
    () =>
      calculateDateRestrictions(
        selectedmanufacturingdate,
        selecteddateofreceipt,
        selectedexpirydate,
        selectedquarantinestartdate
      ),
    [selectedmanufacturingdate, selecteddateofreceipt, selectedexpirydate, selectedquarantinestartdate]
  );

  const isSelectQueryFieldDisabled = useMemo(() => {
    const rawData = selectQuery.data?.data;
    const record = Array.isArray(rawData) ? rawData[0] : rawData;
    return Boolean(record?.isdisabled);
  }, [selectQuery.data]);

  const isFieldDisabled = (fieldName: any) => {
    if (SELECT_QUERY_FIELDS.includes(fieldName)) {
      if (isSelectQueryFieldDisabled) return true;
    }
    return checkIsFieldDisabled(
      fieldName,
      selectedStudyCode,
      selectedmanufacturingdate,
      selectedexpirydate,
      selecteddateofreceipt,
      selectedquarantinestartdate
    );
  };

  const hasCoaFile = useMemo(() => {
    return (
      existingAttachments.some(
        (f: any) => String(f.attachmenttype || f.filetype).toLowerCase() === "coa"
      ) ||
      uploadedFiles.some(
        (f: any) => String(f.attachmenttype).toLowerCase() === "coa"
      )
    );
  }, [existingAttachments, uploadedFiles]);

  /* ------------------------------------------------------------------------ */
  /* 7. (useEffect)                                              */
  /* ------------------------------------------------------------------------ */

  // Document Title & Breadcrumbs
  useEffect(() => {
    document.title = "Edit IP Inventory | ZENTIXS ORBIT";
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Investigational Product", link: IP_PATHS.LIST },
      { text: currentStep === 1 ? "Edit IP Inventory" : "IP Acceptance Sheet" },
    ]);
    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [currentStep]);

  // Preload Dropdown Codelists
  useEffect(() => {
    const load = async () => {
      const result = await loadCodelistsDetailId([
        "IP_STORAGE_CONDITION_OPTIONS",
        "IP_UNIT_OPTIONS",
        "IP_PHYSICAL_CONDITION_OPTIONS",
        "IP_TEMP_LOGGER_STATUS_OPTIONS",
        "IP_TOTAL_CONTAINERS_OPTIONS",
        "Light_Condition_OPTIONS",
        "MANUFACTURER_LABEL_OPTIONS",
        "MODE_OF_RECEIPT_OPTIONS",
        "PACKING_CONDITION_OPTIONS",
        "DOSING_FORM_OPTIONS",
        "Checklist_Type_OPTIONS",
        "CONTAINER_CONDITION_OPTIONS",
      ]);
      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdowns failed to load");
      }
      setCodelistsLoaded(true);
    };
    load();
  }, []);

  // Reset Population Flag on Edit ID Change
  useEffect(() => {
    isPopulatedRef.current = false;
    isCoaPopulatedRef.current = false;
  }, [editId]);

  // Populate Form with Existing Record Data
  useEffect(() => {
    const record = selectQuery.data?.data?.[0];
    if (record && editId && !isPopulatedRef.current) {
      isPopulatedRef.current = true;
      setExistingAttachments(record.attachments || []);
      if (Array.isArray(record.containers) && record.containers.length > 0) {
        originalContainersRef.current = record.containers;
        setContainersList(
          record.containers.map((c: any, idx: number) => ({
            ipdetailid: c.ipdetailid,
            containernumber: c.containernumber || String(idx + 1).padStart(2, "0"),
            initialquantity: c.initialquantity ?? "",
            containertype: c.containertype || null,
            containercondition: c.containercondition || null,
            containerremarks: c.containerremarks || null,
            isusable: c.isusable !== false,
          }))
        );
      } else {
        originalContainersRef.current = [];
        setContainersList([{ containernumber: "01", initialquantity: "", containertype: null, containercondition: null, containerremarks: null, isusable: true }]);
      }
      reset({
        studyid: record.studyid || "",
        ipid: record.ipid || "",
        receiptnumber: record.receiptnumber || "",
        studycode: record.studycode || "",
        sponsorname: record.sponsorname || "",
        sponsorid: record.sponsorid || "",
        ipcategory: record.ipcategory || "",
        productname: record.productname || "",
        genericname: record.genericname || "",
        strength: record.strength || "",
        dosageform: record.dosageform || "",
        labelclaim: record.labelclaim || "",
        regulatoryofstudy: record.regulatoryofstudy || "",
        batchorlot: record.batchorlot || "batch",
        batchlotno: record.batchlotno || "",
        manufacturerlabel: record.manufacturerlabel || "",
        manufacturername: record.manufacturername || "",
        manufacturingdate: formatDateSplit(record.manufacturingdate) || "",
        typeofdate: record.typeofdate || "expiry",
        expirydate: formatDateSplit(record.expirydate) || "",
        lightcondition: record.lightcondition || "",
        receivedqty: record.receivedquantity || 0,
        actualreceivedqty: record.actualreceivedqty || "",
        unit: record.unittype || "",
        totalcontainers: record.totalcontainers ?? null,
        dateofreceipt: formatDateSplit(record.dateofreceipt) || "",
        modeofreceipt: record.modeofreceipt || "",
        packingcondition: record.packingcondition || "",
        packingconditionremarks: record.packingconditionremarks || "",
        qtyforstudy: record.qtyforstudy || "Sufficient",
        qtyforstudyremarks: record.qtyforstudyremarks || "",
        qtyforretention: record.qtyforretention,
        qtyforretentionremarks: record.qtyforretentionremarks || "",
        storagecondition: record.storagecondition || "",
        isdatalogger: String(record.isdatalogger),
        dataloggerid: record.dataloggerid || null,
        dataloggerremarks: record.dataloggerremarks || "",
        loggerprintobtained: record.loggerprintobtained || null,
        tempexcursionacceptable: record.tempexcursionacceptable || null,
        allottedpharmacyid: record.allottedpharmacyid || "",
        quarantineinstrumentid: record.quarantineinstrumentid || "",
        quarantinestartdate: formatDateSplit(record.quarantinestartdate) || "",
        quarantineenddate: formatDateSplit(record.quarantineenddate) || "",
        coareceiptdate: formatDateSplit(record.coareceiptdate) || "",
        comments: record.comments || "",
      });
    }
  }, [selectQuery.data, reset, editId]);

  // Populate Existing COA Details
  useEffect(() => {
    const record = selectQuery.data?.data?.[0];
    if (record?.coadetails && Array.isArray(record.coadetails) && record.coadetails.length > 0 && !isCoaPopulatedRef.current) {
      isCoaPopulatedRef.current = true;
      const mappedCoaList: CoaDetailRow[] = record.coadetails.map((detail: any) => ({
        coaid: detail.coaid ?? null,
        particulars: detail.checklistquestion,
        coaInfo: detail.coaanswer || "yes",
        labelInfo: detail.labelanswer || "yes",
      }));
      originalCoaRef.current = mappedCoaList;
      setCoaList(mappedCoaList);
    }
  }, [selectQuery.data]);

  // Reset Packing Condition Remarks if not unsealed
  useEffect(() => {
    if (!selectedPackingCondition || !isPopulatedRef.current) return;
    if (PACKING_CONDITION_OPTIONS.length > 0) {
      const matchedOpt = PACKING_CONDITION_OPTIONS.find(
        (opt: any) => String(opt.value) === String(selectedPackingCondition)
      );
      const label = matchedOpt ? matchedOpt.label : String(selectedPackingCondition || "");
      if (String(label).trim().toLowerCase() !== "unsealed") {
        setValue("packingconditionremarks", null);
      }
    }
  }, [selectedPackingCondition, setValue]);

  // Reset Study Quantity Remarks
  useEffect(() => {
    if (!isPopulatedRef.current) return;
    if (qtyForStudy && qtyForStudy !== "Insufficient") {
      setValue("qtyforstudyremarks", null);
    }
  }, [qtyForStudy, setValue]);

  // Reset Retention Quantity Remarks
  useEffect(() => {
    if (!isPopulatedRef.current) return;
    if (qtyForRetention && qtyForRetention !== "Insufficient") {
      setValue("qtyforretentionremarks", null);
    }
  }, [qtyForRetention, setValue]);

  // Data Logger Fields Toggle Handling
  useEffect(() => {
    if (!isPopulatedRef.current) return;
    if (isDataLogger === "false") {
      setValue("dataloggerid", null);
      setValue("loggerprintobtained", null);
      setValue("tempexcursionacceptable", null);
    } else if (isDataLogger === "true") {
      const isPrintNoOrNa = selectedLoggerPrint === "no" || selectedLoggerPrint === "na";
      const isTempNoOrNa = selectedTempExcursion === "no" || selectedTempExcursion === "na";
      if (!isPrintNoOrNa && !isTempNoOrNa) {
        setValue("dataloggerremarks", null);
      }
    }
  }, [isDataLogger, selectedLoggerPrint, selectedTempExcursion, setValue]);

  // Date Restrictions & Validation Watchers
  useEffect(() => {
    const mfgDate = parseLocalDate(selectedmanufacturingdate);
    const expDate = parseLocalDate(selectedexpirydate);
    const receiptDate = parseLocalDate(selecteddateofreceipt);
    const coaDate = parseLocalDate(selectedcoareceiptdate);
    const qStartDate = parseLocalDate(selectedquarantinestartdate);
    const qEndDate = parseLocalDate(selectedquarantineenddate);

    if (!mfgDate) return;

    if (expDate && expDate < mfgDate) {
      setValue("expirydate", "", { shouldValidate: true });
      setValue("dateofreceipt", "", { shouldValidate: true });
      if (!isSelectQueryFieldDisabled) setValue("coareceiptdate", "", { shouldValidate: true });
      setValue("quarantinestartdate", "", { shouldValidate: true });
      setValue("quarantineenddate", "", { shouldValidate: true });
      return;
    }

    if (receiptDate) {
      if (receiptDate < mfgDate || (expDate && receiptDate > expDate)) {
        setValue("dateofreceipt", "", { shouldValidate: true });
        if (!isSelectQueryFieldDisabled) setValue("coareceiptdate", "", { shouldValidate: true });
        setValue("quarantinestartdate", "", { shouldValidate: true });
        setValue("quarantineenddate", "", { shouldValidate: true });
        return;
      }
    }

    if (!isSelectQueryFieldDisabled && coaDate && receiptDate) {
      if (coaDate < receiptDate) {
        setValue("coareceiptdate", "", { shouldValidate: true });
      }
    }

    if (qStartDate && receiptDate) {
      if (qStartDate < receiptDate) {
        setValue("quarantinestartdate", "", { shouldValidate: true });
        setValue("quarantineenddate", "", { shouldValidate: true });
        return;
      }
    }

    if (qEndDate && qStartDate) {
      if (qEndDate < qStartDate) {
        setValue("quarantineenddate", "", { shouldValidate: true });
      }
    }
  }, [
    selectedmanufacturingdate,
    selectedexpirydate,
    selecteddateofreceipt,
    selectedcoareceiptdate,
    selectedquarantinestartdate,
    selectedquarantineenddate,
    setValue,
    isSelectQueryFieldDisabled,
  ]);

  // Clear COA error when COA file exists
  useEffect(() => {
    if (hasCoaFile) setCoaError(null);
  }, [hasCoaFile]);

  /* ------------------------------------------------------------------------ */
  /* 8. EVENT HANDLERS & FORM SUBMISSION                                      */
  /* ------------------------------------------------------------------------ */
  const handlePreview = (file: any) => {
    const baseUrl =
      sessionStorage.getItem("baseUrl")?.replace("/api", "") ||
      "http://127.0.0.1:8000";

    if (file instanceof File) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
      setPreviewOpen(true);
      return;
    }

    const path = file?.filepath || file?.path;
    if (!path) return;

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  const handleCoaChange = (index: number, col: "coaInfo" | "labelInfo", value: "yes" | "no" | "na") => {
    setCoaList((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, [col]: value } : item))
    );
  };

  const onSubmit = (data: IpInventoryFormValues) => {
    if (validateContainersRef.current && !validateContainersRef.current()) return;

    if (!hasCoaFile) {
      setCoaError("At least one Certificate of Analysis (COA) document is required.");
      return;
    }

    setCoaError(null);
    setFormData(data);
    setIsUpdateModalOpen(true);
  };

  const onInvalid = () => {
    if (validateContainersRef.current) validateContainersRef.current();
  };

  const handleUpdate = (reasonPayload: ReasonSubmitPayload) => {
    if (!formData || !editId) return;

    const payload = buildUpdatePayload(
      formData,
      editId,
      reasonPayload.reason,
      containersList,
      originalContainersRef.current,
      totalContainerQty,
      coaList,
      originalCoaRef.current,
      deletedAttachmentIds,
      uploadedFiles
    );

    UpdateMutation.mutate(payload, {
      onSuccess: async (response: any) => {
        setDeletedAttachmentIds([]);
        setUploadedFiles([]);
        isPopulatedRef.current = false;
        isCoaPopulatedRef.current = false;
        queryClient.invalidateQueries({ queryKey: ["ipinventory-selectAll"] });
        queryClient.invalidateQueries({ queryKey: ["ipinventory-select-ip"] });
        showToastMessage("success", response?.message || "IP Inventory updated successfully");
        setIsUpdateModalOpen(false);
      },
      onError: (err: any) => {
        showToastMessage("error", err);
      },
    });
  };

  /* ------------------------------------------------------------------------ */
  /* 9. RENDER COMPONENT                                                      */
  /* ------------------------------------------------------------------------ */
  if (selectQuery.isLoading || selectQuery.isFetching) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader />
      </Container>
    );
  }

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>
        {/* Stepper Navigation */}
        <IpInventoryStepper
          currentStep={currentStep}
          onStepChange={handleStepChange}
          isStep1Saved={true}
          editId={editId}
        />

        {currentStep === 1 ? (
          <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
            {/* Section 1: Study & Product Details */}
            <SectionCard
              index={1}
              icon="ri-microscope-line"
              title="Study & Product Details"
              subtitle="Select the study protocol and enter product information."
            >
              <CommonForm fields={getStudyProductTopFields(studyOptions, isFieldDisabled)} control={control} errors={errors} columns={2} />
              <div className="mt-3">
                <CommonForm fields={getStudyProductCategoryFields(selectedStudyCode, editId, ipCategoryOptions)} control={control} errors={errors} columns={1} />
              </div>
              <div className="mt-3">
                <CommonForm fields={getProductDetailsFields(isFieldDisabled, DOSING_FORM_OPTIONS, Light_Condition_OPTIONS, MANUFACTURER_LABEL_OPTIONS, dateRestrictions, selectedTypeOfDate, batchorlot)} control={control} errors={errors} columns={3} />
              </div>
            </SectionCard>

            {/* Section 2: Batch & Delivery Information */}
            <SectionCard
              index={2}
              icon="ri-archive-line"
              title="Batch & Delivery Information"
              subtitle="Provide batch numbers, quantities, and delivery details."
            >
              <CommonForm fields={getBatchDeliveryFields(selectedStudyCode, IP_UNIT_OPTIONS, MODE_OF_RECEIPT_OPTIONS, PACKING_CONDITION_OPTIONS, dateRestrictions, isFieldDisabled, qtyForStudy, qtyForRetention, selectedPackingCondition)} control={control} errors={errors} columns={3} />

              <ContainerManagementSection
                containersList={containersList}
                setContainersList={setContainersList}
                containerError={containerError}
                setContainerError={setContainerError}
                selectedStudyCode={selectedStudyCode}
                setValue={setValue}
                onRegisterValidate={(fn) => {
                  validateContainersRef.current = fn;
                }}
              />
            </SectionCard>

            {/* Section 3: Storage & Condition Assessment */}
            <SectionCard
              index={3}
              icon="ri-temp-cold-line"
              title="Storage & Condition Assessment"
              subtitle="Assess storage requirements and physical condition on receipt."
            >
              <CommonForm fields={getStorageConditionFields(selectedStudyCode, isFieldDisabled)} control={control} errors={errors} columns={2} />
            </SectionCard>

            {/* Section 4: Data Logger Details */}
            <SectionCard
              index={4}
              icon="ri-cpu-line"
              title="Data Logger Details"
              subtitle="Record data logger accompaniment, ID, printout, temperature excursion, and recorder details."
            >
              <CommonForm fields={getDataLoggerFields(selectedStudyCode, isDataLogger === "true", selectedLoggerPrint, selectedTempExcursion)} control={control} errors={errors} columns={4} />
            </SectionCard>

            {/* Section 5: Pharmacy / Quarantine Details */}
            <SectionCard
              index={5}
              icon="ri-shield-check-line"
              title="Pharmacy / Quarantine Details"
              subtitle="Record allotted pharmacy ID, quarantine details, instrument IDs, and storage dates."
            >
              <CommonForm fields={getPharmacyQuarantineFields(selectedStudyCode, dateRestrictions, isFieldDisabled)} control={control} errors={errors} columns={3} />
            </SectionCard>

            {/* Section 6: COA Details */}
            <SectionCard
              index={6}
              icon="ri-file-list-3-line"
              title="COA Details"
              subtitle="Verify COA information and label information compliance across all parameters."
            >
              <div className="mb-3">
                <CommonForm
                  fields={getCoaTopFields(selectedStudyCode, dateRestrictions, isFieldDisabled)}
                  control={control}
                  errors={errors}
                  columns={3}
                />
              </div>
              <CoaDetailsTable
                coaList={coaList}
                onCoaChange={handleCoaChange}
                isDisabled={isSelectQueryFieldDisabled || !selectedStudyCode}
              />
            </SectionCard>

            {/* Section 7: Attachments & Verification */}
            <SectionCard
              index={7}
              icon="ri-attachment-2"
              title="Attachments & Verification"
              subtitle="Upload supporting documents and confirm receipt details."
            >
              <AttachmentManagementSection
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                existingAttachments={existingAttachments}
                setExistingAttachments={setExistingAttachments}
                deletedAttachmentIds={deletedAttachmentIds}
                setDeletedAttachmentIds={setDeletedAttachmentIds}
                selectedStudyCode={selectedStudyCode}
                handlePreview={handlePreview}
                coaError={coaError}
              />

              <Row>
                <Col>
                  <label className="form-label text-dark small fw-bold">
                    Additional Comments / Discrepancies
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter any notes, observations, or discrepancy details..."
                    {...register("comments", validations.ipInventory.comments)}
                    style={{ fontSize: 13, resize: "none", textAlign: "justify" }}
                    disabled={!selectedStudyCode}
                  />
                  {errors.comments && (
                    <div className="text-danger small mt-1">
                      {errors.comments.message}
                    </div>
                  )}
                </Col>
              </Row>
            </SectionCard>

            {/* Sticky Action Footer */}
            <div className={formStyles.stickyFooter}>
              <div className="d-flex justify-content-end gap-2 w-100">
                <Button
                  type="submit"
                  color="primary"
                  outline
                  disabled={UpdateMutation.isPending}
                >
                  Update
                </Button>
                <Button
                  type="button"
                  color="primary"
                  outline
                  onClick={() => handleStepChange(2)}
                >
                  Next
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <IpInventoryAcceptance
            ipid={editId}
            receiptnumber={selectedreceiptnumber}
            onBack={() => handleStepChange(1)}
            isStepperMode={true}
            data={selectQuery.data?.data?.[0]}
          />
        )}

        {/* Reason Modal */}
        <Reason
          isOpen={isUpdateModalOpen}
          toggle={() => setIsUpdateModalOpen(false)}
          action="Update Record"
          id={Number(editId)}
          click={handleUpdate}
          isLoading={UpdateMutation.isPending}
        />

        {/* Document Preview Modal */}
        <FilePreviewModal
          file={previewFile}
          fileUrl={previewFileUrl}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      </Container>
    </div>
  );
};

export default EditIpInventory;
